// Which value on the Test form a place in the report is about, and the other way round
// (TGCLOUD-1868, plan D3 and D6). Pure: no React, no requests.
//
// A TARGET is one thing on the Test form a value of the report lands in, as a string:
//   field:<name>         a Test field by its form name: `field:lab`, `field:testResult`,
//                        `field:testScope`, `field:genesTestedList` (the gene list state)
//   gene:<KEY>           a genes tested row, by its gene key (testRecordModel.rowKeyOf)
//   variant:<findingId>  a variant read from the report, by its findingId
// The same strings applyProposal.choiceId makes, so a target is also a choice's id.
//
// TWO SPELLINGS OF A PATH. A reading's paths are the PROPOSAL's, by position (intake's answer:
// `variants.0.variantC`, `genesTested.2.coverage.status`): position `i` is looked up in the
// answer's proposal, and the variant there names its findingId, the gene row its key. A saved
// Test's are the record's STABLE paths (GET /test-results/:id/anchors: `variants.<findingId>.variantC`,
// `genesTested.BRCA2.coverage.status`, `testResult`), keyed already. Which one a list uses is said,
// never guessed (`pathStyle`): a findingId may be all digits.
//
// THE PROPOSAL'S NAMES FOR A TEST FIELD. `testResultSuggestion` is the Test result the conclusion
// suggested (saved as `testResult`); `geneList.stated` is the count the report states, which the
// gene list state is about; `geneList.withheld.<i>` is a gene of a withheld list, entered by hand
// as a gene row of the same key. `notRead.<i>` and `reportKeys.<i>` are about no field: they have
// no target, and are still selectable in the report. Nor is `individuals.<i>`, a person's row of a
// report about several people, marked while whose the Test is is chosen (TGCLOUD-1868, Phase E4).

import {
  geneKeyOf,
  rowKeyOf
} from 'src/components/pedigree-chart/pedigree-sidebar/sections/clinical-notes-section/forms/test-record/testRecordModel';
import { UNANCHORED_REASONS } from './labReportVerificationVocabulary';

export const PATH_STYLES = Object.freeze({
  proposal: 'proposal',
  stable: 'stable'
});

export const fieldTarget = (name) => `field:${name}`;
export const geneTarget = (key) => `gene:${key}`;
export const variantTarget = (findingId) => `variant:${findingId}`;

const isPlain = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const hasText = (v) => typeof v === 'string' && v.trim() !== '';

// Proposal names that are saved under another field name.
const FIELD_OF = Object.freeze({
  testResultSuggestion: 'testResult'
});
// Paths that are about no Test field.
const NO_FIELD = /^(notRead|reportKeys|individuals)(\.|$)/;

const splitAt = (path, depth) => {
  const parts = path.split('.');
  return {
    head: parts.slice(0, depth).join('.'),
    rest: parts.slice(depth).join('.')
  };
};

/**
 * The target a path lands in, and the rest of the path within it ('' for the target itself):
 * `{ target: 'variant:<findingId>', rest: 'variantC' }`, or null for a path that is about no value
 * on the Test (a note on what was not read, a report key, a position the proposal does not hold).
 *
 * @param {string} path
 * @param {{ proposal?: object, pathStyle?: 'proposal'|'stable', withheldGenes?: object[] }} [context]
 *   `proposal` is the answer's, for a proposal path's positions; `withheldGenes` the answer's
 *   `geneList.withheld`, for `geneList.withheld.<i>`
 * @returns {{ target: string, rest: string } | null}
 */
export function targetOfPath(path, context = {}) {
  if (typeof path !== 'string' || !path) return null;
  const {
    proposal = null,
    pathStyle = PATH_STYLES.proposal,
    withheldGenes = []
  } = context;
  if (NO_FIELD.test(path)) return null;

  const listed = /^(variants|genesTested)\.([^.]+)(?:\.(.+))?$/.exec(path);
  if (listed) {
    const [, list, at, rest = ''] = listed;
    if (pathStyle === PATH_STYLES.stable) {
      if (!at) return null;
      return list === 'variants'
        ? { target: variantTarget(at), rest }
        : { target: geneTarget(geneKeyOf(at)), rest };
    }
    if (!/^\d+$/.test(at)) return null;
    const item = (
      isPlain(proposal) && Array.isArray(proposal[list]) ? proposal[list] : []
    )[Number(at)];
    if (!isPlain(item) && list === 'genesTested') {
      // A gene list withheld whole offers no row, and its genes are the withheld ones, in the
      // reading's order (confidence.js): the reading's row k is withheld gene k, so where it is
      // printed, or why it has no place, is that gene's.
      const gene = (Array.isArray(withheldGenes) ? withheldGenes : [])[
        Number(at)
      ];
      const key = isPlain(gene) ? geneKeyOf(gene.symbol || '') : '';
      return key ? { target: geneTarget(key), rest } : null;
    }
    if (!isPlain(item)) return null;
    if (list === 'variants') {
      return hasText(item.findingId)
        ? { target: variantTarget(item.findingId), rest }
        : null;
    }
    const key = rowKeyOf(item);
    return key ? { target: geneTarget(key), rest } : null;
  }

  const withheld = /^geneList\.withheld\.(\d+)$/.exec(path);
  if (withheld) {
    const gene = (Array.isArray(withheldGenes) ? withheldGenes : [])[
      Number(withheld[1])
    ];
    const key = isPlain(gene) ? geneKeyOf(gene.symbol || '') : '';
    return key ? { target: geneTarget(key), rest: '' } : null;
  }
  if (path === 'geneList.stated' || path.startsWith('geneList.stated.')) {
    return { target: fieldTarget('genesTestedList'), rest: 'stated' };
  }
  if (path === 'geneList' || path.startsWith('geneList.')) return null;

  const { head, rest } = splitAt(path, 1);
  return { target: fieldTarget(FIELD_OF[head] || head), rest };
}

const reasonRank = (reason) => {
  const at = UNANCHORED_REASONS.indexOf(reason);
  return at < 0 ? UNANCHORED_REASONS.length : at;
};

/**
 * Every target a reading (or a saved Test's report) has a value for, with where each of its values
 * is printed: `anchored` paths have boxes in the report (in the anchors' order), `unanchored` ones
 * do not, each with why (rules.UNANCHORED_REASONS; null when the answer gives a value's place but no
 * box could be worked out for it, and gives no reason).
 *
 * @param {{ anchors?, unanchored?, fieldSources?, proposal?, pathStyle?, withheldGenes? }} reading
 * @returns {{ byTarget: Map<string, { anchored: Array<{path, rest}>,
 *   unanchored: Array<{path, rest, reason}> }>, targetOf: (path: string) => string|null }}
 */
export function indexReading({
  anchors = [],
  unanchored = [],
  fieldSources = [],
  proposal = null,
  pathStyle = PATH_STYLES.proposal,
  withheldGenes = []
} = {}) {
  const context = { proposal, pathStyle, withheldGenes };
  const byTarget = new Map();
  const seen = new Set();
  const entryOf = (target) => {
    if (!byTarget.has(target)) {
      byTarget.set(target, { anchored: [], unanchored: [] });
    }
    return byTarget.get(target);
  };
  const boxed = new Set();

  for (const anchor of Array.isArray(anchors) ? anchors : []) {
    if (!isPlain(anchor) || typeof anchor.path !== 'string') continue;
    const rects = Array.isArray(anchor.rects) ? anchor.rects : [];
    if (!rects.length) continue;
    boxed.add(anchor.path);
    if (seen.has(anchor.path)) continue;
    seen.add(anchor.path);
    const at = targetOfPath(anchor.path, context);
    if (at)
      entryOf(at.target).anchored.push({ path: anchor.path, rest: at.rest });
  }
  const placeless = (path, reason) => {
    if (seen.has(path)) return;
    seen.add(path);
    const at = targetOfPath(path, context);
    if (at) {
      entryOf(at.target).unanchored.push({ path, rest: at.rest, reason });
    }
  };
  for (const u of Array.isArray(unanchored) ? unanchored : []) {
    if (isPlain(u) && typeof u.path === 'string' && !boxed.has(u.path)) {
      placeless(u.path, typeof u.reason === 'string' ? u.reason : null);
    }
  }
  // A value the answer gives a place for, with no box drawn for it: said, never left blank.
  for (const f of Array.isArray(fieldSources) ? fieldSources : []) {
    if (isPlain(f) && typeof f.path === 'string' && !boxed.has(f.path)) {
      placeless(f.path, null);
    }
  }
  for (const entry of byTarget.values()) {
    entry.unanchored.sort(
      (a, b) => reasonRank(a.reason) - reasonRank(b.reason)
    );
  }
  return {
    byTarget,
    targetOf: (path) => {
      const at = targetOfPath(path, context);
      return at ? at.target : null;
    }
  };
}

const restMatches = (rest, fields) =>
  !fields || fields.some((f) => rest === f || rest.startsWith(`${f}.`));

/**
 * How a target's value stands against the report:
 *   { state: 'anchored', paths }          printed at a place in the report: `paths` are its boxes'
 *   { state: 'unanchored', reason }       read from the report, with no place to show, and why
 *   { state: 'unverifiable' }             the report could not be shown, so nothing can be checked
 * or null when the report gives the target no value at all (nothing to mark). `fields` keeps only
 * the target's values at those rests (`['variantC', 'hgvsAsPrinted.c']`), for a mark beside one
 * part of a variant or a gene row.
 */
export function anchorStateOf(
  index,
  target,
  { fields = null, reportFailed = false } = {}
) {
  const entry = index && index.byTarget ? index.byTarget.get(target) : null;
  if (!entry) return null;
  const anchored = entry.anchored.filter((a) => restMatches(a.rest, fields));
  const placeless = entry.unanchored.filter((u) => restMatches(u.rest, fields));
  if (!anchored.length && !placeless.length) return null;
  if (reportFailed) return { state: 'unverifiable', paths: [] };
  if (anchored.length) {
    return { state: 'anchored', paths: anchored.map((a) => a.path) };
  }
  return { state: 'unanchored', paths: [], reason: placeless[0].reason };
}

/**
 * The targets a list of paths lands in, each once, in the paths' order.
 * @returns {string[]}
 */
export function targetsOfPaths(index, paths) {
  const out = [];
  for (const path of Array.isArray(paths) ? paths : []) {
    const target = index && index.targetOf ? index.targetOf(path) : null;
    if (target && !out.includes(target)) out.push(target);
  }
  return out;
}
